import Box from "@/common/components/Box";
import Button from "@/common/components/Button";
import TextField from "@/common/components/TextField";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { ChevronsRight } from "lucide-react";
import { useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { FormTransformers } from "@/common/utils/formTransformers";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import SelectController from "@/common/components/SelectController";
import TextArea from "@/common/components/TextArea";
import useDebounce from "@/common/hooks/useDebounce";
import useCreateRewardItem from "../../hooks/useCreateRewardItem";
import { Mapper } from "@/common/services/mapper";
import { createSchema, RewardItemValidationSchema } from "../../schemas/create.schema";
import { ShowHeader } from "@/common/components/ShowHeader";
import { MultipleImageUploader } from "@/common/components/MultipleImageUploader";
import useBlobs from "@/common/hooks/queries/useBlobs";
import { useProductBadges } from "@/common/hooks/queries/useProductBadges";
import { ProductBadgesTransformer } from "@/features/productBadges/services/productBadgesTransformer";
import { UnitTextField } from "@/common/components/UnitTextField";
import { CurrencyTextField } from "@/common/components/CurrencyTextField";
import { useGoBack } from "@/common/hooks/useGoBack";
import useUserPermissions from "@/common/hooks/useUserPermissions";
import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import { DateInput } from "@/common/components/DateInput/DateInput";

const Create = () => {
  const queryClient = useQueryClient()
  const goBack = useGoBack()
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const [searchProductBadge, setSearchProductBadge] = useDebounce('');

  const { mutateAsync: uploadBlob, isPending: uploadBlobIsPending } = useBlobs();

  const {
    mutate,
    isPending: createRewardItemIsPending
  } = useCreateRewardItem({
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['rewardItems']})
      goBack({fallback: `/tmais-reward-items`})
    },
    onError: (error) => {
      if (error.response?.data.errors.base) {
        setMessages(error.response?.data.errors.base)
      } else {
        FormTransformers.errorsTransformer({ setError })(error)
      }
    }
  })

  const { data: productsBadgesOptions } = useProductBadges({
    filters: { search: searchProductBadge },
    mapper: Mapper.mapWithTransformer({ transformer: ProductBadgesTransformer.optionsTransformer() }),
  });

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()
  const userHasPermissionToCreateTmaisProduct =
    isUserPermissionsPending || hasUserPermission('Establishment::Tmais::Products::Create')

  const {
    register,
    control,
    handleSubmit,
    setError,
    formState: { errors }
  } = useForm<RewardItemValidationSchema>({
    resolver: zodResolver(createSchema)
  })

  const onSubmit: SubmitHandler<RewardItemValidationSchema> = (data) => mutate(data);

  return (
    <form
      className="flex flex-col h-[calc(100vh-64px)]"
      onSubmit={handleSubmit(onSubmit)}
      autoComplete="false"
    >
      <ShowHeader
        goBack
        goBackProps={{ fallback: `/tmais-reward-items` }}
        title={
          <div className="flex gap-2 items-center justify-start">
            <h2 className="text-xl font-bold">Itens para resgate</h2>
            <ChevronsRight color='#ffffffb3' />
            <h2 className="text-xl font-bold line-clamp-1">Cadastrar recompensa</h2>
          </div>
        }
        buttons={(
          <div className="flex items-center gap-2">
            <Button
              variant='outline-error'
              onClick={() => goBack({fallback: `/tmais-reward-items`})}
            >
              Cancelar
            </Button>
            {userHasPermissionToCreateTmaisProduct && (
              <Button
                type="submit"
                name="submit"
              >
                Cadastrar recompensa
              </Button>
            )}
          </div>
        )}
      />

      <ErrorMessageWrapper
        className="h-[calc(100vh-128px)]"
        statusCode={userHasPermissionToCreateTmaisProduct ? 200 : 403}
      >
        <Box className="flex flex-col flex-1 overflow-auto gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]">
          <h2 className="text-lg font-bold">Detalhes da Recompensa</h2>

          <div className="flex gap-4">
            <MultipleImageUploader
              label="Anexe uma foto"
              name="equipment-images"
              accept="image/jpeg, image/png"
              uploadBlob={uploadBlob}
              containerClassName="max-w-[288px]"
              control={control}
              isLoading={uploadBlobIsPending}
              fieldName="pictures_attachments_attributes"
              disabled={uploadBlobIsPending || createRewardItemIsPending}
              errorMessage={errors.pictures_attachments_attributes?.message || undefined}
              uploadButtonLayout="floating"
            />
            <div className="flex flex-col gap-4 w-full">
              <div className="flex items-start gap-2">
                <TextField
                  label="Nome"
                  {...register('display_name')}
                  errorMessage={errors?.display_name?.message}
                  markAsRequired
                  className="w-1/2"
                  />
                <TextField
                  label="SKU/Código do produto"
                  {...register('sku')}
                  errorMessage={errors?.sku?.message}
                  className="w-1/2"
                />
              </div>


              <TextArea
                label="Descrição"
                markAsRequired
                errorMessage={errors?.description?.message}
                {...register('description')}
                rows={8}
              />

              <SelectController
                control={control}
                name="product_badges_attributes"
                label="Destacar item com"
                isMulti
                onInputChange={setSearchProductBadge}
                options={productsBadgesOptions || []}
                errorMessage={errors.product_badges_attributes?.message}
              />

            </div>
          </div>

          <h2 className="text-base font-bold">Valor e Estoque </h2>

          <div className="flex items-start gap-2">
            <Controller
              control={control}
              name='reference_price'
              disabled={createRewardItemIsPending}
              render={({ field }) => (
                <CurrencyTextField
                  label="Valor referência (em reais)"
                  {...field}
                  markAsRequired
                  errorMessage={errors?.reference_price?.message || undefined}
                  className="w-1/2"
                />
              )}
            />
            <UnitTextField
              label="Valor para resgate (em pontos)"
              {...register('points_to_redeem')}
              errorMessage={errors?.points_to_redeem?.message}
              disabled={createRewardItemIsPending}
              markAsRequired
              decimals={0}
              className="w-1/2"
            />
          </div>

          <div className="flex items-start gap-2">
            <DateInput
              fieldName="available_from"
              control={control}
              errors={errors}
              errorMessage={errors.available_from?.message || undefined}
              label="Data de disponibilidade"
              markAsRequired
              disabled={createRewardItemIsPending}
              className="w-1/2"
            />
            <DateInput
              fieldName="available_until"
              control={control}
              errors={errors}
              errorMessage={errors.available_until?.message || undefined}
              label="Data de expiração"
              disabled={createRewardItemIsPending}
              className="w-1/2"
            />
          </div>
        </Box>
      </ErrorMessageWrapper>

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </form>
  );
};

export default Create;
